import {
  stripHiddenOpenEndedPhases,
  stripPhasesBeforeAmendmentEffectiveFrom,
  type ReactQuoteRendererData
} from '@sequencehq/quote-content'
import {
  calculateDueOnStartDate,
  calculateQuotePhaseMinimumTotals,
  calculateQuotePhaseSubTotals,
  calculateQuotePriceCommitments,
  calculatePriceTotalsForPhases,
  formatAddress
} from '@sequencehq/utils'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import { adaptPhasesForCalculation } from 'modules/Automations/components/adaptPhasesForCalculation'

import type { Customer, Quote } from '../../../../types'

export const toQuoteRendererData = ({
  quote,
  customer
}: {
  quote: Quote
  customer: Customer
}): ReactQuoteRendererData => {
  const visibleQuote = stripHiddenOpenEndedPhases({
    presentation: JSON.parse(quote.presentation),
    phases: adaptPhasesForCalculation(quote.phases)
  })
  const { presentation, phases, startDate } =
    stripPhasesBeforeAmendmentEffectiveFrom({
      ...visibleQuote,
      dealType: quote.dealType,
      preliminaryBillingStartDate: quote.preliminaryBillingStartDate,
      amendment: quote.amendment
    })
  const totalsStartDate = startDate
    ? apiDatesAdapters.fromApi(startDate)
    : undefined

  const currency = quote.currency || 'USD'

  return {
    ...quote,
    presentation,
    // The duration and end date describe the whole contract, amendment or not
    phases: visibleQuote.phases,
    prospectName: customer.legalName,
    // Deliberately `undefined`, not the empty list `formatAddress` returns:
    // the quote header hides itself on a falsy address, and `[]` is truthy.
    prospectAddress: quote.customerAddress
      ? formatAddress(quote.customerAddress)
      : undefined,
    merchantName: quote.merchantBranding.legalCompanyName,
    merchantEmailAddress: quote.merchantBranding.email,
    merchantAddress: formatAddress(quote.merchantBranding.address),
    merchantLogoUrl: quote.merchantBranding.logoUrl,
    phaseSubTotals: calculateQuotePhaseSubTotals({
      startDate: totalsStartDate,
      phases,
      currency
    }),
    totalDueOnStartDate: calculateDueOnStartDate({
      startDate: totalsStartDate,
      phases,
      currency
    }),
    phaseMinimumTotals: calculateQuotePhaseMinimumTotals({
      startDate: totalsStartDate,
      phases,
      currency
    }),
    priceCommitments: calculateQuotePriceCommitments({ phases }),
    quotePriceTotalsPerPhase: calculatePriceTotalsForPhases({
      startDate: totalsStartDate,
      phases,
      currency
    }),
    expiresAt: quote.expiresAt,
    preliminaryBillingStartDate: quote.preliminaryBillingStartDate,
    defaultDueDateDays: quote.defaultDueDateDays ?? undefined,
    title: quote.title || 'Untitled quote',
    dealType: quote.dealType || 'NEW_BUSINESS',
    currency
  }
}
